import { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRatingSummary } from '@/lib/utils/ratingHelpers';
import type { MenuRatingSummary } from '@/types/api';

const STAR_VALUES = [1, 2, 3, 4, 5] as const;

export function MenuRatingStars({ summary, disabled = false, onRate }: {
  summary: MenuRatingSummary;
  disabled?: boolean;
  onRate: (rating: number) => void;
}) {
  // Hovering previews the fill up to the hovered star, like typical star widgets.
  const [hoverRating, setHoverRating] = useState(0);
  const currentRating = summary.currentUserRating ?? 0;
  const displayRating = !disabled && hoverRating > 0 ? hoverRating : currentRating;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
      <div
        role="group"
        aria-label="Rate this menu"
        className="flex items-center"
        onMouseLeave={() => setHoverRating(0)}
      >
        {STAR_VALUES.map((value) => (
          // aria-disabled (not disabled) so keyboard focus survives the brief
          // disable during a save instead of being dropped to <body>.
          <button
            key={value}
            type="button"
            aria-disabled={disabled}
            aria-pressed={summary.currentUserRating === value}
            aria-label={`Rate ${value} star${value === 1 ? '' : 's'}`}
            onClick={() => { if (!disabled) onRate(value); }}
            onMouseEnter={() => setHoverRating(value)}
            className={cn(
              'rounded-sm p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              disabled && 'opacity-50'
            )}
          >
            <Star
              aria-hidden="true"
              className={cn(
                'h-5 w-5',
                value <= displayRating ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground'
              )}
            />
          </button>
        ))}
      </div>
      <span className="text-xs text-muted-foreground">
        {formatRatingSummary(summary.averageRating, summary.ratingCount)}
      </span>
    </div>
  );
}
